import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  verifyRegisterEmail,
  verifyEmailOTP,
  ChangePassword,
} from "../redux/reduxThunk/thunkForgetPassword/Forget";

const ForgetPassword = () => {
  const [forgetEmail, setForgetEmail] = useState("");
  const [emailOTP, setEmailOTP] = useState("");
  const [disableBtn, setDisableBtn] = useState(false);
  const [confirmOTP, setConfirmOTP] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  const [changePassword, setChangePassword] = useState({
    password: "",
    confirmPassword: "",
    data: "",
  });

  const { forgetData } = useSelector((state) => state.forget);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Toast configuration
  const ShowToast = (message, type = "success") => {
    if (type === "success") {
      toast.success(message, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else if (type === "error") {
      toast.error(message, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else if (type === "info") {
      toast.info(message, {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) return "Email is required";
    if (!emailRegex.test(email)) return "Please enter a valid email address";
    return "";
  };

  // OTP validation
  const validateOTP = (otp) => {
    const otpRegex = /^[0-9]{6}$/; // Assuming 6-digit OTP
    if (!otp.trim()) return "OTP is required";
    if (!otpRegex.test(otp)) return "OTP must be 6 digits";
    return "";
  };

  // Password validation
  const validatePassword = (password, fieldName) => {
    if (!password) return `${fieldName} is required`;
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!/(?=.*[A-Za-z])(?=.*\d)/.test(password)) 
      return "Password must contain both letters and numbers";
    return "";
  };

  // Confirm password validation
  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) return "Please confirm your password";
    if (confirmPassword !== password) return "Passwords do not match";
    return "";
  };

  // Handle blur event
  const handleBlur = (field, value) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    let error = "";
    switch (field) {
      case "forgetEmail":
        error = validateEmail(value);
        break;
      case "emailOTP":
        error = validateOTP(value);
        break;
      case "password":
        error = validatePassword(value, "Password");
        break;
      case "confirmPassword":
        error = validateConfirmPassword(value, changePassword.password);
        break;
      default:
        break;
    }
    
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Validate entire form
  const validateForm = (formType) => {
    const newErrors = {};
    
    if (formType === "email") {
      const emailError = validateEmail(forgetEmail);
      if (emailError) newErrors.forgetEmail = emailError;
    } else if (formType === "otp") {
      const otpError = validateOTP(emailOTP);
      if (otpError) newErrors.emailOTP = otpError;
    } else if (formType === "password") {
      const passwordError = validatePassword(changePassword.password, "Password");
      const confirmError = validateConfirmPassword(changePassword.confirmPassword, changePassword.password);
      
      if (passwordError) newErrors.password = passwordError;
      if (confirmError) newErrors.confirmPassword = confirmError;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Send OTP to email
  const handleForgetPassword = async (e) => {
    e.preventDefault();
    
    // Mark email as touched
    setTouched(prev => ({ ...prev, forgetEmail: true }));
    
    // Validate form
    if (!validateForm("email")) {
      window.alert("Please fix the errors before submitting", "error");
      return;
    }
    
    setDisableBtn(true);
    try {
      const res = await dispatch(verifyRegisterEmail({ forgetEmail })).unwrap();
      if (res?.success) {
        window.alert(res.message || "OTP sent to your email successfully! ✅", "success");
      } else {
        window.alert(res?.message || "Failed to send OTP", "error");
      }
    } catch (err) {
      console.error("Send OTP error:", err);
      const errorMessage = err?.message || 
                          err?.data?.message || 
                          "Failed to send OTP. Please try again.";
      window.alert(errorMessage, "error");
    } finally {
      setDisableBtn(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    // Mark OTP as touched
    setTouched(prev => ({ ...prev, emailOTP: true }));
    
    // Validate form
    if (!validateForm("otp")) {
      window.alert("Please fix the errors before submitting", "error");
      return;
    }
    
    setDisableBtn(true);
    try {
      const res = await dispatch(
        verifyEmailOTP({ emailID: forgetData?.data, emailOTP })
      ).unwrap();
      
      if (res?.success) {
        setConfirmOTP(true);
        setChangePassword((prev) => ({ ...prev, data: res.data }));
        window.alert(res.message || "OTP verified successfully! ✅", "success");
        setErrors({}); // Clear previous errors
        setTouched({}); // Reset touched fields
      } else {
        window.alert(res?.message || "OTP verification failed", "error");
      }
    } catch (err) {
      console.error("Verify OTP error:", err);
      const errorMessage = err?.message || 
                          err?.data?.message || 
                          "Invalid OTP. Please try again.";
      window.alert(errorMessage, "error");
    } finally {
      setDisableBtn(false);
      setEmailOTP("");
    }
  };

  // Handle password input change
  const handleChangePasswordInput = (e) => {
    const { name, value } = e.target;
    setChangePassword((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (touched[name]) {
      let error = "";
      if (name === "password") {
        error = validatePassword(value, "Password");
      } else if (name === "confirmPassword") {
        error = validateConfirmPassword(value, changePassword.password);
      }
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  // Handle password field blur
  const handlePasswordBlur = (e) => {
    const { name, value } = e.target;
    handleBlur(name, value);
  };

  // Submit new password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all password fields as touched
    setTouched({
      password: true,
      confirmPassword: true,
    });
    
    
    setDisableBtn(true);
    try {
      const res = await dispatch(ChangePassword(changePassword)).unwrap();
      
      if (res?.success) {
        window.alert(res.message || "Password changed successfully! ✅", "success");
        setChangePassword({ password: "", confirmPassword: "", data: "" });
        setConfirmOTP(false);
        setErrors({});
        setTouched({});
        
        // Navigate after a short delay
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      } else {
        window.alert(res?.message || "Failed to change password", "error");
      }
    } catch (err) {
      console.error("Change password error:", err);
      const errorMessage = err?.message || 
                          err?.data?.message || 
                          "Failed to change password. Please try again.";
      window.alert(errorMessage, "error");
    } finally {
      setDisableBtn(false);
    }
  };

  // Helper function to render input with validation
  const renderInput = (field, label, type = "text", placeholder = "", value, onChange, onBlur) => (
    <div className="mb-4">
      <label className="block text-gray-700 font-medium mb-2">
        {label}
        {field !== "confirmPassword" && field !== "password" && (
          <span className="text-red-500 ml-1">*</span>
        )}
      </label>
      <input
        type={type}
        name={field}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        className={`w-full px-4 py-3 border-2 rounded-lg outline-none transition-all ${
          errors[field] && touched[field]
            ? "border-red-500 focus:ring-2 focus:ring-red-400"
            : "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        }`}
        required
        minLength={type === "password" ? 8 : undefined}
        maxLength={type === "email" ? 100 : type === "password" ? 50 : 6}
      />
      {errors[field] && touched[field] && (
        <p className="mt-1 text-sm text-red-600">{errors[field]}</p>
      )}
      {field === "password" && !errors[field] && !value && (
        <p className="mt-1 text-xs text-gray-500">
          Must be at least 8 characters with letters and numbers
        </p>
      )}
    </div>
  );

  // Reset all states
  const handleReset = () => {
    setForgetEmail("");
    setEmailOTP("");
    setConfirmOTP(false);
    setChangePassword({ password: "", confirmPassword: "", data: "" });
    setErrors({});
    setTouched({});
  };

  // Handle Enter key press for forms
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!forgetData?.success && !confirmOTP) {
          handleForgetPassword(e);
        } else if (forgetData?.success && !confirmOTP) {
          handleVerifyOTP(e);
        } else if (confirmOTP) {
          handlePasswordSubmit(e);
        }
      }
    };

    document.addEventListener('keypress', handleKeyPress);
    return () => {
      document.removeEventListener('keypress', handleKeyPress);
    };
  }, [forgetData, confirmOTP, forgetEmail, emailOTP, changePassword]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-gray-100 px-4">
      <div className="bg-white shadow-xl rounded-2xl p-8 max-w-md w-full border border-gray-200">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            {confirmOTP ? "🔒 Reset Password" : "🔑 Forgot Password"}
          </h2>
          <p className="text-gray-600">
            {confirmOTP 
              ? "Create a new secure password" 
              : "Enter your email to receive a password reset OTP"
            }
          </p>
        </div>

        {confirmOTP ? (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {renderInput(
              "password",
              "New Password",
              "password",
              "Enter new password",
              changePassword.password,
              handleChangePasswordInput,
              handlePasswordBlur
            )}
            
            {renderInput(
              "confirmPassword",
              "Confirm New Password",
              "password",
              "Confirm new password",
              changePassword.confirmPassword,
              handleChangePasswordInput,
              handlePasswordBlur
            )}
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleReset}
                className="flex-1 bg-gray-200 text-gray-800 font-semibold py-3 rounded-lg hover:bg-gray-300 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={disableBtn}
                className={`flex-1 font-semibold py-3 rounded-lg transition-all duration-300 ${
                  disableBtn
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {disableBtn ? "Updating..." : "Update Password"}
              </button>
            </div>
          </form>
        ) : (
          <div>
            {forgetData?.success ? (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">OTP sent to:</span> {forgetData?.data}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Please check your email for the 6-digit OTP code
                  </p>
                </div>
                
                {renderInput(
                  "emailOTP",
                  "Enter OTP",
                  "text",
                  "Enter 6-digit OTP",
                  emailOTP,
                  (e) => setEmailOTP(e.target.value),
                  () => handleBlur("emailOTP", emailOTP)
                )}
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="flex-1 bg-gray-200 text-gray-800 font-semibold py-3 rounded-lg hover:bg-gray-300 transition-all duration-300"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={disableBtn}
                    className={`flex-1 font-semibold py-3 rounded-lg transition-all duration-300 ${
                      disableBtn
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                  >
                    {disableBtn ? "Verifying..." : "Verify OTP"}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleForgetPassword} className="space-y-4">
                {renderInput(
                  "forgetEmail",
                  "Email Address",
                  "email",
                  "Enter your registered email",
                  forgetEmail,
                  (e) => setForgetEmail(e.target.value),
                  () => handleBlur("forgetEmail", forgetEmail)
                )}
                
                <button
                  type="submit"
                  disabled={disableBtn}
                  className={`w-full font-semibold py-3 rounded-lg transition-all duration-300 ${
                    disableBtn
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  {disableBtn ? "Sending OTP..." : "Send OTP"}
                </button>
                
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    ← Back to Login
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
        
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            {confirmOTP 
              ? "Make sure your new password is strong and unique"
              : "Enter your registered email to receive a password reset OTP"
            }
          </p>
          {!confirmOTP && forgetData?.success && (
            <p className="text-xs text-blue-600 text-center mt-2">
              OTP will expire in 10 minutes
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;